import React, { Component } from 'react';
import './pentities.css';
import './caseStudy.css';
import problemImage1 from './assets/main.webp';
import problemImage2 from './assets/5.webp';
import solutionImage from './assets/6.webp';
import NavBar from './NavBar';

interface CaseStudyProps {}

interface CaseStudyState {}

class CaseStudy extends Component<CaseStudyProps, CaseStudyState> {
  render() {
    return (
      <div className="pentities-page challenge-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/15420266_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">The Challenge</span>
              <h1 className="pentities-hero-title">
                Your business runs on Microsoft 365.<br />So why does work still slip through the cracks?
              </h1>
              <p className="pentities-hero-description">
                Every enterprise already owns the tools. Yet documents scatter across SharePoint, Teams and
                Outlook, approvals stall between systems, and generic AI never quite fits the way your teams
                actually work. This is where modern operations break down and where Meta‑365 begins.
              </p>
              <div className="pentities-hero-cta">
                <a href="#solution" className="pentities-hero-cta-btn">See How We Solve It</a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">The Digital Transformation Challenge</span>
              <h2 className="pentities-gendesc-title">The tools are in place. The friction is everywhere.</h2>
              <p>
                Microsoft 365 has become the backbone of the modern enterprise but adopting the platform is not
                the same as mastering it. Critical documents and processes still live in silos, spread across
                SharePoint, Teams, OneDrive, email and legacy systems that were never designed to work as one.
              </p>
              <p>
                The result is a quiet, expensive drag on the business. Finance, HR and Legal teams lose thousands
                of hours a year to repetitive document work. Approvals wait on the right person to notice an email.
                Governance becomes a matter of hope rather than design. And the AI meant to help rarely understands
                the specific rules, context and vocabulary of your organisation.
              </p>
              <p>
                Understanding exactly where and why this happens is the first step. Below are the three challenges
                we see in almost every organisation and the operational cost they carry.
              </p>
            </div>
          </div>
        </section>

        {/* The Challenges dark section */}
        <section id="challenges" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Where the work breaks down</span>
            <h2 className="pentities-docmgmt-title">Three challenges standing between your teams and their best work</h2>
            <p className="pentities-docmgmt-lead">
              None of these are tooling problems in the traditional sense the tools are already there. They are
              problems of fragmentation, rigidity and lost context that compound quietly across every department.
            </p>

            <div className="pentities-docmgmt-block challenge-block">
              <h3>01 · Fragmented content and siloed workflows</h3>
              <figure className="challenge-figure challenge-figure-right challenge-figure-lg">
                <img loading="lazy" decoding="async" src={problemImage1} alt="Fragmented content spread across Microsoft 365" />
                <figcaption>Work spread thin across SharePoint, Teams, Outlook and legacy systems with no single source of truth.</figcaption>
              </figure>
              <p>
                Enterprises depend on Microsoft 365, yet the documents and workflows that run the business remain
                scattered across SharePoint, Teams, OneDrive, email and legacy systems. That fragmentation slows
                teams down, raises compliance risk, and pushes up the cost of manual work especially for Finance,
                HR and Legal, who carry the heaviest repetitive document load.
              </p>
              <ul>
                <li><b>Scattered information</b> the same document lives in five places, and none of them is the source of truth.</li>
                <li><b>Compliance risk</b> consistent governance is nearly impossible when content spans disconnected systems.</li>
                <li><b>Time drain</b> thousands of hours a year disappear into manual document handling.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>02 · Complex processes and limited AI adaptability</h3>
              <p>
                Fragmentation compounds complexity. Real processes cross multiple layers of validation,
                certification and approval, spanning different systems and stakeholders. And while most companies
                have tried to bring AI in, generic pre-trained models rarely adapt to domain-specific needs leaving the most important workflows manual, slow and error-prone.
              </p>
              <ul>
                <li><b>Multi-layer processes</b> approval chains stretch across systems no one owns end to end.</li>
                <li><b>Generic AI</b> one-size-fits-all models miss the rules and vocabulary that actually matter.</li>
                <li><b>Manual workflows</b> the highest-value processes stay stubbornly hands-on.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block challenge-block">
              <h3>03 · Lack of flexibility and governance challenges</h3>
              <figure className="challenge-figure challenge-figure-left challenge-figure-md">
                <img loading="lazy" decoding="async" src={problemImage2} alt="Governance and permission challenges across systems" />
                <figcaption>Lost context and tangled permissions make governance a matter of hope rather than design.</figcaption>
              </figure>
              <p>
                Without AI and automation that reflect organisational rules and industry requirements, businesses
                cannot enforce consistent governance. Metadata and context are lost as content moves between
                systems, permissions become redundant and hard to manage, and disconnected tools break the link
                between a task and the content it depends on.
              </p>
              <ul>
                <li><b>Lost metadata</b> context disappears the moment content crosses a system boundary.</li>
                <li><b>Permission chaos</b> redundant, overlapping access rules that no one can confidently audit.</li>
                <li><b>Misalignment</b> tasks disconnected from the right content, people and deadlines.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Operational Impact white section */}
        <section id="impact" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">The operational impact</span>
            <h2 className="pentities-workflow-title">What fragmentation actually costs the business</h2>
            <p className="pentities-workflow-lead">
              Modern enterprises struggle to control their own workflows inside Microsoft 365. Teams jump between
              SharePoint, Teams, Outlook and aging systems just to finish simple tasks. Information gets lost,
              approvals stall, and compliance gets harder to enforce. The cost shows up in four ways.
            </p>

            <div className="pentities-workflow-block">
              <h3>Escalating costs</h3>
              <p>
                Operational expenses keep climbing as manual processes consume resources that could be automated.
                Every workaround is a recurring cost that never appears on a single line item but adds up across
                the year.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Massive time loss</h3>
              <p>
                Teams are stuck managing documents, contracts and approvals by hand. That drains capacity from the
                people you hired to think, decide and serve customers and delays the decisions that move the
                business forward.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Productivity drain</h3>
              <p>
                Employees spend more time searching for information and navigating systems than doing the
                high-value work only they can do. Knowledge workers become part-time file clerks.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Compliance risk</h3>
              <p>
                Inconsistent processes and disconnected systems make it nearly impossible to keep clean audit
                trails and enforce policy. When the auditor asks, the answer takes days instead of clicks.
              </p>
            </div>
          </div>
        </section>

        {/* The Solution dark section */}
        <section id="solution" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">How Meta‑365 solves it</span>
            <h2 className="pentities-docmgmt-title">One connected platform, built on the Microsoft 365 you already own</h2>
            <p className="pentities-docmgmt-lead">
              Meta‑365 delivers a suite of applications designed specifically to close these gaps integrating
              natively with Microsoft 365 to unify content, enforce governance, and put intelligent automation in
              the hands of the people who own each process.
            </p>

            <div className="pentities-docmgmt-block challenge-block">
              <figure className="challenge-figure challenge-figure-right challenge-figure-md">
                <img loading="lazy" decoding="async" src={solutionImage} alt="The Meta-365 connected solution inside Microsoft 365" />
                <figcaption>Content, workflows, AI and governance working as one inside the Microsoft 365 your teams already use.</figcaption>
              </figure>
              <ul>
                <li><b>Unified content management</b> break down silos with seamless integration across SharePoint, Teams and Outlook, without moving a single file out of your tenant.</li>
                <li><b>Intelligent search &amp; discovery</b> find the right information instantly with AI-powered, permission-aware search across every platform.</li>
                <li><b>Automated workflows</b> streamline approvals, validations and document processes with no-code automation that runs on your own data.</li>
                <li><b>Governance &amp; compliance</b> keep metadata, permissions and audit trails consistent across the whole organisation.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Solutions grid */}
        <section id="solutions" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">Meta‑365 Solutions</span>
            <h2 className="pentities-modules-title">Match the challenge to the solution</h2>
            <p className="pentities-modules-lead">
              Each product targets one of the gaps above and together they form a single, connected platform
              inside Microsoft Teams.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/pentities" className="pentities-module-card">
                <h3>pEntities Platform</h3>
                <p>
                  Turn every contract, case, HR record or project into a living workspace documents, emails,
                  tasks, people and AI in one place, natively inside Microsoft Teams.
                </p>
                <span className="pentities-module-link">Explore pEntities →</span>
              </a>

              <a href="/solutions/automation" className="pentities-module-card">
                <h3>Workflow Automation</h3>
                <p>
                  Replace manual coordination with no-code workflows that trigger on your own Microsoft 365 data,
                  enforce deadlines, and run themselves configured by the people who own the process.
                </p>
                <span className="pentities-module-link">Explore Automation →</span>
              </a>

              <a href="/solutions/pshareemail" className="pentities-module-card">
                <h3>PShareEmail</h3>
                <p>
                  Bring email into the workflow save messages to SharePoint, OneDrive or Teams and keep every
                  conversation connected to the record it belongs to.
                </p>
                <span className="pentities-module-link">Explore PShareEmail →</span>
              </a>

              <a href="/solutions/docsearch" className="pentities-module-card">
                <h3>DocSearch</h3>
                <p>
                  Find and manage content across OneDrive, SharePoint and Teams from one place unified,
                  permission-aware search and record management that ends the hunt for the right file.
                </p>
                <span className="pentities-module-link">Explore DocSearch →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default CaseStudy;
